import { useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import Cached from '@mui/icons-material/Cached'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'

import type { SortOrder } from 'common/api/v1/types'
import {
    AiConversationStatus,
    type AiConversationSummary,
    ListAiConversationSortableField,
} from 'common-billing-server/types'
import type { Query } from 'common/query'

import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import { makeAiConversationPath } from '../../routes/path'
import { FilterType } from 'common-ui/components/Filter'
import {
    Column,
    DataFetchingPaginatedTable,
    type FieldFilterConfig,
    RefreshableDataFetchingPaginatedTable,
} from '../common/Table'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import type { PaginatedDataFetchingFilter } from '../common/hooks/hook-paginated-data-fetching'
import { RotatingButton } from '../common/RotatingButton'
import Wrapper from '../common/Wrapper'

type ConversationFieldFilter = { latestStatus: string }

const statusFieldFilters: Array<FieldFilterConfig<ConversationFieldFilter>> = [
    {
        filterName: 'latestStatus',
        label: 'Status',
        type: FilterType.select,
        options: [
            { name: 'Completed', value: AiConversationStatus.completed },
            { name: 'Blocked', value: AiConversationStatus.blocked },
            { name: 'Failed', value: AiConversationStatus.failed },
            { name: 'Running', value: AiConversationStatus.running },
        ],
    },
]

export const ListAiConversationsPage = () => {
    const billingApi = useBillingApi()
    const navigate = useNavigate()
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const { addFailedOperation, errorBanner } = useFailedOperationBanner()

    const columns: Column<AiConversationSummary, ListAiConversationSortableField>[] = useMemo(
        () => [
            {
                title: 'Conversation',
                valueForColumn: (conversation) => conversation.title,
                sortField: ListAiConversationSortableField.title,
            },
            {
                title: 'Customer',
                valueForColumn: (conversation) => conversation.customerName,
                sortField: ListAiConversationSortableField.customerName,
            },
            {
                title: 'Installation',
                valueForColumn: (conversation) => conversation.installationHost,
                sortField: ListAiConversationSortableField.installationHost,
            },
            {
                title: 'Edge user',
                valueForColumn: (conversation) => conversation.edgeUsername,
                sortField: ListAiConversationSortableField.edgeUsername,
            },
            {
                title: 'Turns',
                valueForColumn: (conversation) => conversation.turnCount.toString(),
            },
            {
                title: 'Status',
                valueForColumn: (conversation) => <Chip size="small" label={conversation.latestStatus} />,
            },
            {
                title: 'Last activity (UTC)',
                valueForColumn: (conversation) => formatDateTime(conversation.updatedAt),
                sortField: ListAiConversationSortableField.updatedAt,
            },
        ],
        []
    )

    async function fetchConversations({
        filter,
        order,
        ...query
    }: Query<
        PaginatedDataFetchingFilter<Record<string, never>, ConversationFieldFilter>,
        SortOrder<ListAiConversationSortableField>
    >) {
        try {
            const latestStatus = filter?.fieldFilters?.latestStatus
            return await billingApi.listAiConversations({
                ...query,
                filter: {
                    searchName: filter?.searchString,
                    ...(latestStatus ? { latestStatus: latestStatus as AiConversationStatus } : {}),
                },
                order,
            })
        } catch (error) {
            addFailedOperation({
                id: 'list-ai-conversations',
                message: 'Failed fetching AI conversations',
                error,
                retryFn: tableRef.current?.refreshData,
            })
            throw error
        }
    }

    return (
        <Wrapper
            actions={
                <RotatingButton
                    key="refresh"
                    tooltip="Refresh"
                    onClick={() => tableRef.current?.refreshData()}
                    Component={<Cached />}
                />
            }
        >
            <Paper>
                <DataFetchingPaginatedTable
                    myRef={tableRef}
                    api={fetchConversations}
                    columns={columns}
                    emptyRowsMessage="No AI conversations match the selected filters"
                    leftAlignFilters
                    fieldFilters={statusFieldFilters}
                    onRowClicked={(conversation) =>
                        navigate(makeAiConversationPath(conversation.installationId, conversation.threadId))
                    }
                    searchParameters={{
                        searchBarPlaceholder: 'Search by title, customer, installation, or Edge user...',
                    }}
                />
            </Paper>
            {errorBanner}
        </Wrapper>
    )
}
